import {
  Accessibility,
  Link2,
  Moon,
  Pause,
  RotateCcw,
  Sun,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { Locale } from "../types";

type TextSize = "100" | "125" | "150" | "200";
type ColorMode = "dark" | "light" | "contrast";

type AccessibilitySettings = {
  textSize: TextSize;
  colorMode: ColorMode;
  underlineLinks: boolean;
  reduceMotion: boolean;
};

const STORAGE_KEY = "restore-booking-accessibility-v1";

const DEFAULT_SETTINGS: AccessibilitySettings = {
  textSize: "100",
  colorMode: "dark",
  underlineLinks: false,
  reduceMotion: false,
};

const labels = {
  he: {
    open: "נגישות",
    title: "התאמות נגישות",
    close: "סגירת תפריט הנגישות",
    textSize: "גודל טקסט",
    normal: "רגיל",
    displayColors: "צבעי תצוגה",
    dark: "כהה",
    light: "בהיר",
    contrast: "ניגודיות גבוהה",
    underlineLinks: "הדגשת קישורים",
    reduceMotion: "עצירת הנפשות",
    enabled: "פעיל",
    disabled: "כבוי",
    reset: "איפוס התאמות",
  },
  en: {
    open: "Accessibility",
    title: "Accessibility settings",
    close: "Close accessibility settings",
    textSize: "Text size",
    normal: "Default",
    displayColors: "Display colors",
    dark: "Dark",
    light: "Light",
    contrast: "High contrast",
    underlineLinks: "Underline links",
    reduceMotion: "Stop animations",
    enabled: "On",
    disabled: "Off",
    reset: "Reset settings",
  },
} as const;

function readSettings(): AccessibilitySettings {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return DEFAULT_SETTINGS;

    const value = JSON.parse(saved) as Partial<AccessibilitySettings>;
    return {
      textSize: ["100", "125", "150", "200"].includes(value.textSize ?? "")
        ? (value.textSize as TextSize)
        : DEFAULT_SETTINGS.textSize,
      colorMode: ["dark", "light", "contrast"].includes(value.colorMode ?? "")
        ? (value.colorMode as ColorMode)
        : DEFAULT_SETTINGS.colorMode,
      underlineLinks: value.underlineLinks === true,
      reduceMotion: value.reduceMotion === true,
    };
  } catch {
    return DEFAULT_SETTINGS;
  }
}

export function AccessibilityMenu({ locale }: { locale: Locale }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(readSettings);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const text = labels[locale];

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.rbTextSize = settings.textSize;
    root.dataset.rbTheme = settings.colorMode;
    root.classList.toggle("rb-underline-links", settings.underlineLinks);
    root.classList.toggle("rb-reduce-motion", settings.reduceMotion);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };

    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  const update = <Key extends keyof AccessibilitySettings>(
    key: Key,
    value: AccessibilitySettings[Key],
  ) => setSettings((current) => ({ ...current, [key]: value }));

  return (
    <div className={`a11y-widget${open ? " open" : ""}`}>
      {open && (
        <section
          className="a11y-panel"
          id="accessibility-settings"
          role="dialog"
          aria-modal="false"
          aria-labelledby="accessibility-title"
        >
          <header className="a11y-panel-heading">
            <div>
              <Accessibility size={22} aria-hidden="true" />
              <h2 id="accessibility-title">{text.title}</h2>
            </div>
            <button
              ref={closeRef}
              type="button"
              className="a11y-icon-button"
              aria-label={text.close}
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus();
              }}
            >
              <X size={22} aria-hidden="true" />
            </button>
          </header>

          <fieldset className="a11y-group">
            <legend>{text.textSize}</legend>
            <div className="a11y-choice-grid text-size-choices">
              {(["100", "125", "150", "200"] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  aria-pressed={settings.textSize === size}
                  onClick={() => update("textSize", size)}
                >
                  <span aria-hidden="true">A</span>
                  {size === "100" ? text.normal : `${size}%`}
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="a11y-group">
            <legend>{text.displayColors}</legend>
            <div className="a11y-choice-grid color-mode-choices">
              <button
                type="button"
                aria-pressed={settings.colorMode === "dark"}
                onClick={() => update("colorMode", "dark")}
              >
                <Moon size={19} aria-hidden="true" />
                {text.dark}
              </button>
              <button
                type="button"
                aria-pressed={settings.colorMode === "light"}
                onClick={() => update("colorMode", "light")}
              >
                <Sun size={19} aria-hidden="true" />
                {text.light}
              </button>
              <button
                type="button"
                aria-pressed={settings.colorMode === "contrast"}
                onClick={() => update("colorMode", "contrast")}
              >
                <span className="contrast-symbol" aria-hidden="true" />
                {text.contrast}
              </button>
            </div>
          </fieldset>

          <div className="a11y-toggle-list">
            <button
              type="button"
              aria-pressed={settings.underlineLinks}
              onClick={() => update("underlineLinks", !settings.underlineLinks)}
            >
              <Link2 size={20} aria-hidden="true" />
              <span>{text.underlineLinks}</span>
              <strong>{settings.underlineLinks ? text.enabled : text.disabled}</strong>
            </button>
            <button
              type="button"
              aria-pressed={settings.reduceMotion}
              onClick={() => update("reduceMotion", !settings.reduceMotion)}
            >
              <Pause size={20} aria-hidden="true" />
              <span>{text.reduceMotion}</span>
              <strong>{settings.reduceMotion ? text.enabled : text.disabled}</strong>
            </button>
          </div>

          <button
            type="button"
            className="a11y-reset"
            onClick={() => setSettings(DEFAULT_SETTINGS)}
          >
            <RotateCcw size={19} aria-hidden="true" />
            {text.reset}
          </button>
        </section>
      )}

      <button
        ref={triggerRef}
        type="button"
        className="a11y-trigger"
        aria-expanded={open}
        aria-controls="accessibility-settings"
        onClick={() => setOpen((current) => !current)}
      >
        <Accessibility size={25} aria-hidden="true" />
        <span>{text.open}</span>
      </button>
    </div>
  );
}
